type RedirectWithRetryParams = {
  url: string;
  /** The function to push the URL to. eg: router.push or push from next/navigation. */
  push: (href: string) => void;
  /** Callback when all retry attempts have failed. */
  onFailure?: () => void;
  maxAttempts?: number;
  delayMs?: number;
};

export const redirectWithRetry = ({
  url,
  push,
  onFailure,
  maxAttempts = 3,
  delayMs = 15000,
}: RedirectWithRetryParams) => {
  let attempt = 0;
  let timeoutId: NodeJS.Timeout;
  let visibilityChangeHandler: (() => void) | null = null;

  const tryRedirect = () => {
    clearTimeout(timeoutId);
    // Remove any existing visibility change listener
    if (visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', visibilityChangeHandler);
      visibilityChangeHandler = null;
    }

    attempt++;
    if (attempt > maxAttempts) {
      if (onFailure) {
        onFailure();
      }
      return;
    }

    const prevUrl = window.location.href;

    try {
      push(url);

      // Check if document is hidden and pause retry. This happens when open new tab for Vipps
      const checkVisibilityAndRetry = () => {
        if (document.hidden) {
          // Tab is hidden, set up event listener for when it becomes visible
          visibilityChangeHandler = () => {
            if (!document.hidden) {
              // Tab became visible, remove listener and check if retry is needed
              document.removeEventListener('visibilitychange', visibilityChangeHandler!);
              visibilityChangeHandler = null;
              const currentUrl = window.location.href;
              if (currentUrl === prevUrl) {
                tryRedirect();
              }
            }
          };
          document.addEventListener('visibilitychange', visibilityChangeHandler);
        } else {
          // Tab is visible, proceed with normal retry check
          timeoutId = setTimeout(() => {
            const currentUrl = window.location.href;
            if (currentUrl === prevUrl) {
              tryRedirect();
            }
          }, delayMs);
        }
      };

      checkVisibilityAndRetry();
    } catch (err) {
      timeoutId = setTimeout(() => tryRedirect(), delayMs);
    }
  };

  tryRedirect();
};
